import React, { useState } from 'react';
import { Box, Dialog, Stack, Typography } from '@mui/material';

import { Button } from '../../../../components/core';
import { useDeleteDomainMutation } from '../../../../store/modules/merchant/api';
import { Domain } from '../../../../store/modules/merchant/types';
import { toast } from '../../../../util/toast';
import { useMerchant } from '../../../../contexts/MerchantContext';

interface Props {
  open: boolean;
  onClose: () => void;
  domain: Domain;
}

const DomainDeleteModal = ({ open, onClose, domain }: Props) => {
  const { merchantId } = useMerchant();
  const [deleteDomain] = useDeleteDomainMutation();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteDomain = async () => {
    if (!merchantId) return;

    setIsDeleting(true);
    await deleteDomain({ merchantId, urlId: domain.id })
      .unwrap()
      .then(() => {
        onClose();
        toast.success('Merchant domain has been deleted');
        setIsDeleting(false);
      })
      .catch(() => {
        toast.error(
          'An error occurred while deleting the merchant domain. Please try again later.',
        );
        setIsDeleting(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: '90%', md: '733px' },
          p: 3,
        },
      }}
    >
      <Typography variant="xl" fontWeight="semibold" sx={{ mb: 3 }}>
        Are you sure you want to delete this merchant domain?
      </Typography>

      <Typography variant="large" fontWeight="semibold" sx={{ mb: 3 }}>
        This domain will be removed and no longer approved for authorized access upon deletion.
      </Typography>

      <Box sx={{ mb: 6 }}>
        <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontWeight: 500 }}>
          Domain URL
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'black', overflowWrap: 'anywhere' }}>
          {domain.url}
        </Typography>
      </Box>

      <Stack spacing={2} sx={{ '& button': { height: '48px' } }}>
        <Button
          text={isDeleting ? 'Deleting...' : 'Delete Merchant Domain'}
          disabled={isDeleting}
          onClick={handleDeleteDomain}
          theme="primary"
        />
        <Button text="Cancel" disabled={isDeleting} onClick={onClose} theme="secondary" />
      </Stack>
    </Dialog>
  );
};

export default DomainDeleteModal;
